import { useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasStores } from '../stores/CanvasContext';

// Color property keys found on entity.data across all entity kinds.
const COLOR_KEYS = ['backgroundColor', 'textColor', 'borderColor', 'strokeColor', 'color'] as const;

/**
 * Scans all entities in the active tree and collects every color value
 * currently in use. Reads directly from entity.data instead of instantiating
 * toolbar actions (O(entities) property reads vs O(entities × actions)).
 */
export function useUsedColors(): string[] {
  const { store } = useCanvasStores();

  // Build a stable color-key from entity data — only re-compute when colors actually change.
  const colorKey = useSelector(store, s => {
    const nodes = s.context.nodes;
    let key = '';
    for (const e of Object.values(nodes)) {
      const d = e.data;
      for (const k of COLOR_KEYS) {
        const v = d[k];
        if (typeof v === 'string' && v.length > 0) key += v + ',';
      }
    }
    return key;
  });

  return useMemo(() => {
    if (!colorKey) return [];
    const colors = new Set<string>();
    for (const part of colorKey.split(',')) {
      if (part.length > 0) colors.add(part);
    }
    return Array.from(colors).sort();
  }, [colorKey]);
}
